import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Printer, Instagram, Mail, Phone, MapPin, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navigation = [
  { name: "Accueil", href: "/" },
  { name: "Services", href: "/services" },
  { name: "Réalisations", href: "/realisations" },
  { name: "À propos", href: "/a-propos" },
];

export default function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-background/95 backdrop-blur-sm header-scrolled" : "bg-transparent"
        }`}
        data-testid="main-header"
      >
        <nav className="container-custom flex items-center justify-between py-4 md:py-5">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="flex items-center gap-1">
              <span className="accent-dot accent-green" />
              <span className="accent-dot accent-red" />
              <span className="accent-dot accent-yellow" />
            </div>
            <span className="text-xl md:text-2xl font-bold tracking-tight">ART PRINT</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm uppercase tracking-wider transition-colors hover:text-foreground ${
                  location.pathname === item.href ? "text-foreground font-medium" : "text-muted"
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/devis"
              className="btn-primary"
              data-testid="header-cta-devis"
            >
              Demander un devis
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu"
            data-testid="mobile-menu"
          >
            <button
              className="absolute top-4 right-6 p-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <X size={24} />
            </button>
            <nav className="flex flex-col items-center gap-6">
              {navigation.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className={`text-2xl font-bold ${
                      location.pathname === item.href ? "text-foreground" : "text-muted"
                    }`}
                    data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Link to="/devis" className="btn-primary mt-4" data-testid="mobile-cta-devis">
                  Demander un devis
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-background" data-testid="main-footer">
        <div className="container-custom section-padding">
          <div className="grid md:grid-cols-4 gap-12 md:gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-[hsl(var(--accent-green))]" />
                  <span className="w-2 h-2 bg-[hsl(var(--accent-red))]" />
                  <span className="w-2 h-2 bg-[hsl(var(--accent-yellow))]" />
                </div>
                <span className="text-xl font-bold tracking-tight">ART PRINT</span>
              </div>
              <p className="text-background/70 max-w-md mb-6">
                Atelier de sérigraphie, d'infographie et d'imprimerie en Martinique. 
                Impression sur tous supports : T-shirt, tasse, gourde, casquette, plexiglass et plus.
              </p>
              <Link to="/devis" className="btn-outline border-background text-background hover:bg-background hover:text-foreground">
                Demander un devis
              </Link>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold text-sm uppercase tracking-wider mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-background/50 mt-0.5 flex-shrink-0" />
                  <span className="text-background/70">97200 Fort-de-France<br />Martinique</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-background/50 flex-shrink-0" />
                  <span className="text-background/70">0696 60 13 17</span>
                </li>
                <li className="flex items-center gap-3">
                  <Clock size={18} className="text-background/50 flex-shrink-0" />
                  <span className="text-background/70">Lun - Ven: 8h - 18h</span>
                </li>
              </ul>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-bold text-sm uppercase tracking-wider mb-6">Navigation</h4>
              <ul className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-background/70 hover:text-background transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/devis"
                    className="text-background/70 hover:text-background transition-colors"
                  >
                    Demander un devis
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-background/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-background/50 text-sm">
              © {new Date().getFullYear()} ART PRINT. Tous droits réservés.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-background/50 hover:text-background transition-colors"
                data-testid="social-instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="mailto:contact@artprint.ch"
                className="text-background/50 hover:text-background transition-colors"
                data-testid="social-email"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
